import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import client from '../../utils/axiosInstance';

const initialState = {
  currentSurvey: null,
  currentSurveyForAnswer: null,
  demographicsSurvey: null,
  demographicsSurvey2: null,
  questionsSurvey: [],
  status: 'idle',
  error: null,
  errorResponse: null,
};

/**
 * Normaliza el error de axios a { status, data, message } para que los thunks
 * rechazados conserven el cuerpo de la respuesta. Sin esto el componente solo
 * recibe "Request failed with status code 409" y no puede distinguir entre
 * "ya se respondio" y "se alcanzo el maximo de respuestas".
 *
 * @param error
 * @returns {{status: (number|undefined), data: *, message: string}}
 */
const toErrorPayload = (error) => ({
  status: error?.response?.status,
  data: error?.response?.data,
  message: error?.message ?? 'Error de red',
});

export const fetchSurveyByIdAndCompanyId = createAsyncThunk(
  'surveys/fetchByIdAndCompanyId',
  async ({ surveyId, companyId }) => {
    const { data } = await client.get(`ShowQuestion/${surveyId}/${companyId}`);

    return data;
  }
);

export const fetchSurveyForAnswer = createAsyncThunk(
  'surveys/fetchSurveyForAnswer',
  async ({ surveyId, companyId }) => {
    const { data } = await client.get(`Survey/${surveyId}/${companyId}`);

    return data;
  }
);

export const fetchSurveyDemographic = createAsyncThunk(
  'surveys/fetchSurveyDemographic',
  async ({ surveyId, companyId }) => {
    const { data } = await client.get(`Demographics/${surveyId}/${companyId}`);

    return data;
  }
);

export const fetchSurveyDemographic2 = createAsyncThunk(
  'surveys/fetchSurveyDemographic2',
  async ({ surveyId, answerId }) => {
    const url = answerId
      ? `NewDemographics/${surveyId}/${answerId}`
      : `NewDemographics/${surveyId}`;
    const { data } = await client.get(url);

    return data;
  }
);

export const fetchSurveyQuestion = createAsyncThunk(
  'surveys/fetchSurveyQuestion',
  async ({ surveyId, companyId, answerId }, { rejectWithValue }) => {
    const url = answerId
      ? `ShowQuestionAnswer/${surveyId}/${companyId}/${answerId}`
      : `ShowQuestionAnswer/${surveyId}/${companyId}`;

    try {
      const { data } = await client.get(url);
      return data;
    } catch (error) {
      return rejectWithValue(toErrorPayload(error));
    }
  }
);

export const fetchSurveyForAnswerPersonal = createAsyncThunk(
  'surveys/fetchSurveyForAnswerPersonal',
  async ({ surveyId, companyId, answerId }, { rejectWithValue }) => {
    try {
      const { data } = await client.get(
        `Survey/${surveyId}/${companyId}/${answerId}`
      );

      return data;
    } catch (error) {
      return rejectWithValue(toErrorPayload(error));
    }
  }
);

export const storeSurvey = createAsyncThunk(
  'surveys/storeSurvey',
  async (survey, { rejectWithValue }) => {
    try {
      const { data } = await client.post('answerSurvey', survey);

      return data;
    } catch (error) {
      return rejectWithValue(toErrorPayload(error));
    }
  }
);

const surveysSlice = createSlice({
  name: 'surveys',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSurveyByIdAndCompanyId.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSurveyByIdAndCompanyId.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentSurvey = action.payload;
      })
      .addCase(fetchSurveyByIdAndCompanyId.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });

    builder
      .addCase(fetchSurveyDemographic.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSurveyDemographic.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.demographicsSurvey = action.payload;
      })
      .addCase(fetchSurveyDemographic.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });

    builder
      .addCase(fetchSurveyDemographic2.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSurveyDemographic2.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.demographicsSurvey2 = action.payload;
      })
      .addCase(fetchSurveyDemographic2.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });

    builder
      .addCase(fetchSurveyQuestion.pending, (state) => {
        state.status = 'loading';
        state.errorResponse = null;
      })
      .addCase(fetchSurveyQuestion.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.questionsSurvey = action.payload;
      })
      .addCase(fetchSurveyQuestion.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.message ?? action.error.message;
        state.errorResponse = action.payload ?? null;
      });

    builder
      .addCase(fetchSurveyForAnswer.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSurveyForAnswer.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentSurveyForAnswer = action.payload;
      })
      .addCase(fetchSurveyForAnswer.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });

    builder
      .addCase(fetchSurveyForAnswerPersonal.pending, (state) => {
        state.status = 'loading';
        state.errorResponse = null;
      })
      .addCase(fetchSurveyForAnswerPersonal.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentSurveyForAnswer = action.payload;
      })
      .addCase(fetchSurveyForAnswerPersonal.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.message ?? action.error.message;
        state.errorResponse = action.payload ?? null;
      });

    builder
      .addCase(storeSurvey.pending, (state) => {
        state.status = 'loading';
        state.errorResponse = null;
      })
      .addCase(storeSurvey.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentSurvey = action.payload;
      })
      .addCase(storeSurvey.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.message ?? action.error.message;
        state.errorResponse = action.payload ?? null;
      });
  },
});

export const selectCurrentSurvey = (state) => state.surveys.currentSurvey;
export const selectCurrentSurveyForAnswer = (state) =>
  state.surveys.currentSurveyForAnswer;
export const selectSurveysStatus = (state) => state.surveys.status;
export const selectSurveyDemograpichs = (state) =>
  state.surveys.demographicsSurvey;
export const selectSurveyDemograpichs2 = (state) =>
  state.surveys.demographicsSurvey2;
export const selectSurveyQuestion = (state) => state.surveys.questionsSurvey;
export const selectSurveysErrorResponse = (state) =>
  state.surveys.errorResponse;

export default surveysSlice.reducer;
